import React, { Key, useContext, useEffect, useState } from "react";
import { Group, Box, Collapse, UnstyledButton, Tooltip } from "@mantine/core";
import { ChevronDown } from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { NavigationLinkProps } from "../../providers/Router";
import { AuthContext } from "../../providers/AuthProvider";
import UserAccessControlUtils from "../../utils/UserAccessControlUtils";

interface TooltipContentProps {
  classes: Record<Key, string>;
  cx: any;
  link: NavigationLinkProps;
  setCurrentPath: (path: string) => void;
}
interface NavLinkProps extends TooltipContentProps {
  expanded: boolean;
  showIcon?: boolean;
}

const useNavigationHandler = (
  link: NavigationLinkProps,
  setCurrentPath: (path: string) => void
): (() => void) => {
  const navigate = useNavigate();
  return () => {
    if (link.isExternal) {
      window.open(link.path, "_blank");
    } else {
      setCurrentPath(link.path);
      navigate(link.path);
    }
  };
};

const TooltipContent: React.FC<TooltipContentProps> = ({
  classes,
  cx,
  link,
  setCurrentPath,
}) => {
  const location = useLocation();
  const handleNavigation = useNavigationHandler(link, setCurrentPath);
  const currentLocation = location.pathname.split("/").filter(Boolean);

  return (
    <Tooltip
      key={link.id}
      label={link.title}
      position="right"
      withArrow
      transitionDuration={0}
    >
      {link.isExternal ? (
        <UnstyledButton
          onClick={handleNavigation}
          className={cx(classes.linkActionIcon)}
        >
          <link.icon aria-hidden="true" />
        </UnstyledButton>
      ) : (
        <Link to={link.path}>
          <UnstyledButton
            className={cx(classes.linkActionIcon, {
              [classes.activeActionIcon]: currentLocation.includes(
                link.path.split("/").filter(Boolean)[0]
              ),
            })}
          >
            <link.icon aria-hidden="true" />
          </UnstyledButton>
        </Link>
      )}
    </Tooltip>
  );
};

const NavLink: React.FC<NavLinkProps> = ({
  expanded,
  classes,
  cx,
  link,
  setCurrentPath,
  showIcon,
}) => {
  const location = useLocation();
  const currentLocation = location.pathname.split("/").filter(Boolean);
  const [opened, setOpened] = useState(false);
  const hasChildren = link.children && link.children.length > 0;
  const handleNavigation = useNavigationHandler(link, setCurrentPath);

  useEffect(() => {
    const paths = link.children?.map((child) => child.path) || [];
    const isCurrentPath = paths.some((path) =>
      location.pathname.includes(path)
    );
    if (isCurrentPath) {
      setOpened(true);
    } else {
      setOpened(false);
    }
  }, [link, location.pathname]);

  const auth = useContext(AuthContext);

  if (link.sidebar === false && link.secondarySidebar === false) {
    return null;
  }

  if (!auth) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {expanded && (
        <>
          {link.isExternal ? (
            <UnstyledButton
              onClick={handleNavigation}
              className={cx(classes.link)}
            >
              <Group noWrap spacing={0} style={{ width: "100%" }}>
                {showIcon && (
                  <link.icon
                    color="currentColor"
                    className={classes.linkIcon}
                    aria-hidden="true"
                  />
                )}
                {expanded && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}
                  >
                    <Box>{link.title}</Box>
                  </Box>
                )}
              </Group>
            </UnstyledButton>
          ) : (
            <UnstyledButton
              onClick={() =>
                hasChildren ? setOpened((o) => !o) : handleNavigation()
              }
              className={cx(classes.link, {
                [classes.linkActive]: currentLocation.includes(
                  link.path.split("/").filter(Boolean)[0]
                ),
              })}
              style={{
                width: showIcon ? "auto" : "100%",
                fontWeight: showIcon ? 600 : 400,
                lineHeight: "18px",
              }}
            >
              <Group noWrap spacing={0} style={{ width: "100%" }}>
                {showIcon && (
                  <link.icon
                    color="currentColor"
                    className={classes.linkIcon}
                    aria-hidden="true"
                  />
                )}
                {expanded && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexGrow: 1,
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {link.title}
                    </Box>
                    {link.path === "/coming-soon" && (
                      <span
                        style={{
                          marginLeft: "10px",
                          fontSize: 8,
                          fontWeight: 500,
                          backgroundColor: "#ED7787",
                          padding: "2px 4px",
                          borderRadius: "2px",
                          color: "white",
                        }}
                      >
                        NEW
                      </span>
                    )}
                  </Box>
                )}
                {hasChildren && (
                  <ChevronDown
                    size={16}
                    className={classes.chevron}
                    style={{
                      transform: opened ? `rotate(-180deg)` : "none",
                      flexShrink: 0,
                      marginLeft: 4,
                    }}
                  />
                )}
              </Group>
            </UnstyledButton>
          )}
          {hasChildren && (
            <Collapse
              in={opened}
              style={{
                marginLeft: "23px",
                paddingLeft: "12px",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                width: showIcon ? "auto" : "calc(100% - 30px)",
                borderLeft: "1px solid rgba(0, 0, 0, 0.05)",
              }}
            >
              {link.children?.map((child) => {
                if (
                  !UserAccessControlUtils.hasFeature(
                    auth.user_features,
                    child.requiredFeature
                  )
                )
                  return null;
                if (
                  !UserAccessControlUtils.hasPermission(
                    auth.permissions,
                    child.requiredPermissions
                  )
                )
                  return null;
                return (
                  <NavLink
                    key={child.id}
                    link={child}
                    expanded={expanded}
                    setCurrentPath={setCurrentPath}
                    classes={classes}
                    cx={cx}
                    showIcon={false}
                  />
                );
              })}
            </Collapse>
          )}
        </>
      )}

      {!expanded &&
        (!hasChildren ? (
          <TooltipContent
            link={link}
            classes={classes}
            cx={cx}
            setCurrentPath={setCurrentPath}
          />
        ) : (
          link.children?.map((child) => {
            if (
              !UserAccessControlUtils.hasFeature(
                auth.user_features,
                child.requiredFeature
              )
            )
              return null;
            if (
              !UserAccessControlUtils.hasPermission(
                auth.permissions,
                child.requiredPermissions
              )
            )
              return null;
            return child.sidebar === false &&
              child.secondarySidebar === false ? null : (
              <TooltipContent
                key={child.id}
                {...{
                  expanded,
                  classes,
                  cx,
                  link: child,
                  setCurrentPath,
                  showIcon,
                }}
              />
            );
          })
        ))}
    </>
  );
};

export default NavLink;
